// Gemeinsame UI-Bausteine des Moduls "Auftragsbestaetigung erfassen" (Muster:
// modules/einvoicing/components/ui.js). Modul-Akzentfarbe: Indigo (passend zur
// Registry-Kachel 'indigo' + ClipboardCheck-Icon).
// Statusmaschine (ab_documents.status): processing -> needs_review | no_ruleset | failed;
// needs_review -> approved | rejected; reprocess setzt auf processing zurueck.
// Abgleichs-Zustaende (compare-Endpoint): match=gruen, diff=amber, fill=blau, missing=rot.

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/** Dokument-Status -> Chip-Ton (Ampel gemaess Bauplan). */
export const STATUS_TONE = {
  processing: 'blue',
  needs_review: 'amber',
  no_ruleset: 'slate',
  failed: 'red',
  approved: 'green',
  rejected: 'red',
};

const CHIP_CLS = (isDarkMode) => ({
  blue: isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700',
  green: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
  amber: isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700',
  red: isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700',
  slate: isDarkMode ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-600',
  indigo: isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700',
});

const DOT_CLS = {
  blue: 'bg-blue-500', green: 'bg-emerald-500', amber: 'bg-amber-500',
  red: 'bg-red-500', slate: 'bg-slate-400', indigo: 'bg-indigo-500',
};

export const Chip = ({ tone = 'slate', isDarkMode, children, title }) => (
  <span title={title} className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${CHIP_CLS(isDarkMode)[tone] || CHIP_CLS(isDarkMode).slate}`}>
    {children}
  </span>
);

/** Status-Chip mit Punkt (Ampel) — bewusst als eigener Baustein, damit der Punkt
 * konsistent vor jedem Statustext steht (Design-Vorgabe "Status-Chips mit Punkt"). */
export const StatusBadge = ({ status, isDarkMode, t }) => {
  const tone = STATUS_TONE[status] || 'slate';
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${CHIP_CLS(isDarkMode)[tone]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${DOT_CLS[tone]} ${status === 'processing' ? 'animate-pulse' : ''}`} />
      {t(`ab.status.${status}`)}
    </span>
  );
};

/** Abgleichs-Badge je Zeile (state: ok | diff | not_found). */
export const CompareBadge = ({ state, isDarkMode, t }) => {
  const tone = state === 'ok' ? 'green' : state === 'diff' ? 'amber' : 'slate';
  return <Chip tone={tone} isDarkMode={isDarkMode}>{t(`ab.compare.state.${state}`)}</Chip>;
};

export const Card = ({ isDarkMode, className = '', children }) => (
  <div className={`rounded-2xl p-5 ${isDarkMode ? 'bg-white/[0.06] border border-white/[0.12]' : 'bg-white border border-slate-200 shadow-sm'} ${className}`}>
    {children}
  </div>
);

export const SectionTitle = ({ isDarkMode, children, right }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{children}</h3>
    {right}
  </div>
);

/** KPI-Kachel — Zahl gross, Klick filtert die Liste (Muster einvoicing KpiTile). */
export const KpiTile = ({ isDarkMode, label, value, tone = 'slate', onClick, Icon }) => {
  const toneText = {
    blue: isDarkMode ? 'text-blue-300' : 'text-blue-600',
    amber: isDarkMode ? 'text-amber-300' : 'text-amber-600',
    red: isDarkMode ? 'text-red-300' : 'text-red-600',
    green: isDarkMode ? 'text-emerald-300' : 'text-emerald-600',
    slate: isDarkMode ? 'text-white/80' : 'text-slate-800',
  }[tone];
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`text-left rounded-2xl p-4 min-w-0 transition-all ${isDarkMode
        ? 'bg-white/[0.06] border border-white/[0.12] hover:bg-white/10'
        : 'bg-white border border-slate-200 shadow-sm hover:border-slate-300'} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />}
        <span className={`text-xs leading-tight break-words min-w-0 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${toneText}`}>{value}</div>
    </button>
  );
};

/** Farbtoene fuer eine Abgleichs-Zelle in der Positionstabelle (match/diff/fill/missing). */
export const compareCellCls = (state, isDarkMode) => {
  switch (state) {
    case 'match': return isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50';
    case 'diff': return isDarkMode ? 'bg-amber-500/15' : 'bg-amber-50';
    case 'fill': return isDarkMode ? 'bg-blue-500/15' : 'bg-blue-50';
    case 'missing': return isDarkMode ? 'bg-red-500/10' : 'bg-red-50';
    default: return '';
  }
};

// de-DE-Formatierung ueber zentrale Helfer (RULES: EUR 2 Nachkommastellen, Datum dd.mm.yyyy).
export const money = (v, currency = 'EUR') =>
  v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('de-DE', { style: 'currency', currency: currency || 'EUR' });

export const qty = (v) =>
  v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('de-DE', { maximumFractionDigits: 3 });

export const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE');
};

/** ISO-Datum (YYYY-MM-DD) fuer <input type="date">; leer wenn nicht parsebar. */
export const toIsoDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

export const inputCls = (isDarkMode) => `px-3 py-2 rounded-xl text-sm outline-none transition-colors ${isDarkMode
  ? 'bg-white/10 border border-white/15 text-white placeholder-white/40 focus:border-indigo-400'
  : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-indigo-500'}`;

export const btnPrimary = (isDarkMode) => `inline-flex items-center justify-center min-h-[40px] px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode
  ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
  : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`;

export const btnGhost = (isDarkMode) => `inline-flex items-center justify-center min-h-[40px] px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode
  ? 'bg-white/10 hover:bg-white/15 text-white/80 border border-white/10'
  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`;

export const btnDanger = (isDarkMode) => `inline-flex items-center justify-center min-h-[40px] px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode
  ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-400/20'
  : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'}`;

/** Kleine Icon-Schaltflaeche (Bearbeiten/Loeschen in Tabellenzeilen). */
export const iconBtnCls = (isDarkMode, tone = 'default') => {
  const base = 'inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  if (tone === 'danger') {
    return `${base} ${isDarkMode ? 'text-red-300 hover:bg-red-500/15' : 'text-red-600 hover:bg-red-50'}`;
  }
  return `${base} ${isDarkMode ? 'text-white/50 hover:bg-white/10 hover:text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'}`;
};

// ==================== UI-PARITAET (Welle 3): Shell/Dashboard/Monitoring-Helfer ====================
// Gemeinsame, framework-lose Formatierungs-/Darstellungshelfer fuer AbShell, AbDashboard,
// AbMonitoring, AbMailDetail, AbMyReports (Agent FE-1). Bewusst OHNE t()-Abhaengigkeit, damit sie
// in reinen Berechnungen (z. B. Sortierung) wiederverwendbar bleiben — die Uebersetzung passiert
// beim Aufrufer per t(`ab.shell.time${unit}Ago`, { n }).

/** Initialen fuer Avatar-Kacheln (Name bevorzugt, sonst E-Mail-Lokalteil). */
export const initials = (s) => {
  if (!s) return '—';
  const name = String(s).replace(/<[^>]*>/g, '').trim() || String(s);
  const parts = name.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return String(s).slice(0, 2).toUpperCase();
  const a = parts[0][0] ?? '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0][1] ?? '');
  return (a + b).toUpperCase();
};

/**
 * Relative Zeitangabe in Bestandteilen (kein fertiger String — der Aufrufer uebersetzt via t()).
 * @returns {{unit: 'now'|'min'|'hours'|'days', n: number} | null}
 */
export const relativeTimeParts = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return { unit: 'now', n: 0 };
  if (min < 60) return { unit: 'min', n: min };
  const h = Math.floor(min / 60);
  if (h < 24) return { unit: 'hours', n: h };
  return { unit: 'days', n: Math.floor(h / 24) };
};

/** de-DE Datum+Uhrzeit (dd.mm.yyyy, HH:MM) — zentraler Helfer statt ad-hoc toLocaleString in Views. */
export const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Ton fuer ab_mail_messages.status (claimed/processed/failed/ignored) — eigene Skala, nicht STATUS_TONE (Dokumente). */
export const mailStatusTone = (status) => {
  switch (status) {
    case 'processed': return 'green';
    case 'failed': return 'red';
    case 'ignored': return 'slate';
    case 'claimed': return 'blue';
    default: return 'slate';
  }
};

/** Kleiner Icon-Button mit optionalem Zaehl-Badge (Header: Melden-Flag, Benachrichtigungs-Glocke). */
export const HeaderIconButton = ({ isDarkMode, onClick, active, badge = 0, title, ariaLabel, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={ariaLabel || title}
    aria-current={active ? 'page' : undefined}
    className={`relative inline-flex items-center justify-center w-9 h-9 rounded-xl transition-colors flex-shrink-0 ${
      active
        ? (isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600')
        : (isDarkMode ? 'text-white/60 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800')
    }`}
  >
    {children}
    {badge > 0 && (
      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
        {badge > 9 ? '9+' : badge}
      </span>
    )}
  </button>
);

/**
 * Ereignis-Label mit Fallback fuer die 2 NEUEN Events aus Welle 3 ('reported'/'report_resolved'),
 * die (noch) NICHT in ab.de.js/ab.en.js liegen (Datei-Eigentuemer in dieser Welle: niemand darf
 * sie anfassen) — eigener Namespace `ab.shell.event.*` nur fuer diese beiden, sonst wird das
 * bestehende `ab.event.*`-Vokabular wiederverwendet (keine Dopplung).
 */
export const eventLabel = (t, event) => (
  event === 'reported' || event === 'report_resolved' ? t(`ab.shell.event.${event}`) : t(`ab.event.${event}`)
);

/** Generische Seiten-Navigation (Vorbild AbDocumentList-Pager) — fuer neue Listen (Posteingang, Monitoring). */
export const Pager = ({ page, totalPages, total, onPage, isDarkMode, t }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4">
      <span className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
        {t('ab.shell.pageInfo', { page, totalPages, total })}
      </span>
      <div className="flex gap-2">
        <button type="button" onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} className={btnGhost(isDarkMode)}>
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className={btnGhost(isDarkMode)}>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Bestaetigungs-Dialog als Overlay (ersetzt window.confirm fuer Freigeben/Loeschen —
 * Design-Vorgabe "modern und hochwertig"). Reiner Anzeige-Baustein, keine eigene Logik.
 */
export const ConfirmDialog = ({ isDarkMode, t, title, message, confirmLabel, cancelLabel, tone = 'primary', busy, onConfirm, onCancel, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true">
    <div className={`w-full max-w-md rounded-2xl p-6 ${isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-slate-200 shadow-xl'}`}>
      <h3 className={`text-base font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
      {message && <p className={`text-sm mb-4 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{message}</p>}
      {children}
      <div className="flex justify-end gap-2 mt-5">
        <button type="button" className={btnGhost(isDarkMode)} onClick={onCancel} disabled={busy}>
          {cancelLabel || t('common.cancel')}
        </button>
        <button type="button" className={tone === 'danger' ? btnDanger(isDarkMode) : btnPrimary(isDarkMode)} onClick={onConfirm} disabled={busy}>
          {confirmLabel || t('common.confirm')}
        </button>
      </div>
    </div>
  </div>
);
